/*
 * Copyright (c) 2024-2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Component, EventEmitter, Output} from '@angular/core';
import {ICellEditorAngularComp} from 'ag-grid-angular';
import {ICellEditorParams} from 'ag-grid-community';
import {DropdownHelperService} from '../../service/dropdown-helper.service';
import {CommonHelperService} from '../../service/common-helper.service';
import {commitValueToRow} from './ag-grid-cell-editor.util';

@Component({
    selector: 'app-object-dropdown-editor',
    styles: [`:host { display: block; width: 100%; height: 100%; }`],
    template: `
        @if (maxSelectionCount === 1) {
          <!-- Single-select dropdown -->
          <p-select class="set-width" [options]="availableOptions" [(ngModel)]="selectedOption"
            (onChange)="onSelectionChange()"
            [appendTo]="overlayAppendTarget"
            [style]="editorStyle"
            [filter]="enableFilter" [filterBy]="optionLabelProperty"
            [showClear]="allowClear"
            placeholder="Select option" [optionLabel]="optionLabelProperty">
          </p-select>
        } @else {
          <!-- Multi-select dropdown -->
          <p-multiSelect [options]="availableOptions"
            [(ngModel)]="selectedOptions"
            (onChange)="onSelectionChange()"
            [appendTo]="overlayAppendTarget"
            defaultLabel="Select options" [style]="{'max-width': '100%'}"
            display="chip" [optionLabel]="optionLabelProperty">
          </p-multiSelect>
        }
        `,
    standalone: false
})
export class ObjectDropdownCellEditorComponent implements ICellEditorAngularComp {
    public overlayAppendTarget: 'body' = 'body';
    public selectedOption: any | null = null;
    public selectedOptions: any[] = [];
    public allowedValueKey: string;
    public valueFormatString: string;
    public optionLabelProperty: string; // The property to use for optionLabel (either allowedValueKey or 'formattedLabel')
    public availableOptions: any[] = [];
    public allowedValuesMap: {[key: string]: string} = {};
    public maxSelectionCount = 1;
    // Opt-in search-as-you-type on the single-select p-select (like the KTS milestone editor).
    // Off by default so existing consumers that don't request it are unchanged.
    public enableFilter = false;
    // Opt-in clear ("x") control, so a chosen option can be taken back out again. Without it a cell can
    // only ever be changed to another option, never emptied. Off by default for the same reason as
    // enableFilter: the other consumers of this editor should not change behaviour.
    public allowClear = false;
    public editorStyle: Record<string, string> = {width: '100%', 'max-width': '100%'};
    private params: ICellEditorParams | null = null;
    /** The editor params actually in force: selector-supplied where present, else the ColDef's. */
    private editorConfig: Record<string, any> = {};
    @Output() valueChange = new EventEmitter<any>();

    agInit(params: ICellEditorParams): void {
        this.params = params;
        const columnWidth = params.column?.getActualWidth?.();
        if (columnWidth && columnWidth > 0) {
            this.editorStyle = {width: `${columnWidth}px`, 'max-width': '100%'};
        } else {
            this.editorStyle = {width: '100%', 'max-width': '100%'};
        }
        // A cellEditorSelector may hand this editor params that differ from the ColDef's static
        // cellEditorParams - the aggregator IO matrix narrows availableOptions per row that way, so a
        // signal already assigned to another row is not offered. ag-grid merges the selector's params
        // into the params passed here, so read those first and fall back to the ColDef for the callers
        // that only declare cellEditorParams. Reading the ColDef alone silently ignored the selector.
        this.editorConfig = this.resolveEditorConfig(params);

        // availableOptions contains the objects for the dropdown
        const rawOptions = this.editorConfig.availableOptions || [];
        this.allowedValueKey = this.editorConfig.allowedValueKey;
        this.valueFormatString = this.editorConfig.valueFormatString;
        this.allowedValuesMap = this.editorConfig.allowedValuesMap || {};
        this.maxSelectionCount = this.editorConfig.maxSelectionCount ?? 1;
        this.enableFilter = this.editorConfig.filter ?? false;
        this.allowClear = this.editorConfig.allowClear ?? false;

        // If valueFormatString is provided, format each option and add a formattedLabel property
        if (this.valueFormatString && rawOptions.length > 0 && typeof rawOptions[0] === 'object') {
            this.availableOptions = rawOptions.map((option: any) => {
                // Format the label using the format string.
                // The format string expects the base object to be called 'val', so we pass {val: option}.
                // This allows format strings like "${val.temperatureClass}" or "${val.operatingTemperatureMin}°C"
                // to access nested properties using dot notation via getDeepValue in evaluateFormatString.
                // Example: "${val.temperatureClass}: ${val.operatingTemperatureMax}°C"
                const formattedLabel = CommonHelperService.evaluateFormatString(this.valueFormatString, {val: option});

                // Return a new object with the formatted label
                return {
                    ...option,
                    formattedLabel: formattedLabel
                };
            });
            // Use formattedLabel as the optionLabel
            this.optionLabelProperty = 'formattedLabel';
        } else {
            // No format string, use the original options and allowedValueKey
            this.availableOptions = rawOptions;
            this.optionLabelProperty = this.allowedValueKey;
        }

        // A maxSelectionCount of 0 means unlimited (multi-select)
        const isMultiSelect = this.maxSelectionCount != 1;

        // Find the matching object(s) from availableOptions based on the current value
        const currentValue = params.value;

        if (isMultiSelect) {
            // For multi-select, handle array of values or semicolon-separated string
            if (currentValue) {
                const values = DropdownHelperService.splitMultiSelectValue(currentValue);

                // Find matching objects for each value
                this.selectedOptions = values.map(val => {
                    if (this.allowedValueKey) {
                        const matchingOption = DropdownHelperService.findOptionByAllowedValueKey(this.availableOptions,
                                                                                                 val,
                                                                                                 this.allowedValueKey);
                        return matchingOption || val;
                    }
                    return val;
                }).filter(v => v != null);
            } else {
                this.selectedOptions = [];
            }
        } else {
            // For single-select
            if (currentValue && this.allowedValueKey) {
                // Find the object that matches the current value's allowedValueKey property
                const matchingOption = DropdownHelperService.findOptionByAllowedValueKey(this.availableOptions,
                                                                                         currentValue,
                                                                                         this.allowedValueKey);
                this.selectedOption = matchingOption || currentValue;
            } else {
                this.selectedOption = currentValue || null;
            }
        }
    }

    /**
     * Merges the params the editor was actually given over the ColDef's declared cellEditorParams.
     * Only the keys this editor understands are considered, so nothing from ag-grid's own params
     * (value, column, api, ...) can be mistaken for configuration.
     */
    private resolveEditorConfig(params: ICellEditorParams): Record<string, any> {
        const declared: Record<string, any> = (params.colDef as any)?.cellEditorParams ?? {};
        const supplied: Record<string, any> = params as any;
        const config: Record<string, any> = {...declared};
        for (const key of ['availableOptions', 'allowedValueKey', 'valueFormatString', 'allowedValuesMap',
                           'maxSelectionCount', 'filter', 'allowClear', 'isListManagerStructure']) {
            if (supplied[key] !== undefined) {
                config[key] = supplied[key];
            }
        }
        return config;
    }

    getValue() {
        // Check if this is a ListManager structure (has label and description)
        const isListManagerStructure = this.editorConfig.isListManagerStructure || false;
        const isMultiSelect = this.maxSelectionCount != 1;

        if (isMultiSelect) {
            // For multi-select, return array of values or semicolon-separated string
            if (isListManagerStructure) {
                // For ListManager, extract labels and join with semicolon
                const values = this.selectedOptions.map(opt => DropdownHelperService.extractValueByKey(opt, this.allowedValueKey))
                                                   .filter(v => v != null);
                return values.join('; ');
            } else {
                // For actual objects, return array of objects
                return this.selectedOptions;
            }
        } else {
            // For single-select
            if (isListManagerStructure) {
                // For ListManager objects (like {label: 'M6', description: 'bolt_M6'}),
                // the row data stores a string (the label), so extract it
                return DropdownHelperService.extractValueByKey(this.selectedOption, this.allowedValueKey);
            } else {
                // For actual objects (like ISOWire), the row data stores the full object, so return it
                return this.selectedOption;
            }
        }
    }

    onSelectionChange(): void {
        const value = this.getValue();
        commitValueToRow(this.params, value);
        this.valueChange.emit(value);
    }

    refresh(params: any): boolean {
        return false;
    }

    isPopup(): boolean {
        return false;
    }
}

